import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../../api/queryClient'
import type { ConversationMessage } from '../../api/schema'

/**
 * A read is what a HUMAN saw — spec 2026-09-10-05.
 *
 * The server used to stamp `read_at` as a side effect of `GET /conversations/{uid}`,
 * which made "read" mean "a browser fetched the thread": a live hint landing in
 * a background tab, a window-refocus refetch or a PATCH invalidation all cleared
 * the badge for a manager who never looked. Only the client knows what was
 * actually on screen, so the client asserts it.
 *
 * The bar for "seen", all three at once:
 *
 *   - the bubble is on screen — half of it, or its top edge for a bubble taller
 *     than the pane it scrolls in, because a long voice transcript must still
 *     be ackable;
 *   - the document is `visible` AND focused for the whole dwell. Leaving the
 *     tab, minimizing or locking the screen cancels the pending dwell; coming
 *     back restarts it;
 *   - it stayed that way for DWELL_MS. A message that flies past while someone
 *     scrolls to the bottom is not read.
 *
 * Completed uids are batched on a short debounce — the console acks a handful of
 * bubbles at once, never one request per message — and never sent twice.
 */

/** How long a bubble must stay on screen, visible and focused, to count. */
const DWELL_MS = 1000
/** How long to collect completed uids before one request goes out. */
const DEBOUNCE_MS = 500
/** Half the bubble, which is what "on screen" means for a normal message. */
const VISIBLE_RATIO = 0.5

/** Whether someone could actually be looking at the page right now. */
function looking(): boolean {
    if (typeof document === 'undefined') return false
    if (document.visibilityState !== 'visible') return false

    // `hasFocus` is the half that catches a visible-but-unfocused window — a
    // second monitor the manager is not on, the console behind a dialog.
    return typeof document.hasFocus === 'function' ? document.hasFocus() : true
}

/**
 * Whether an entry counts as on screen: half the bubble, or — for a bubble
 * taller than the window it is read THROUGH, where half can never intersect —
 * its top edge being inside that window.
 *
 * That window is the viewport intersected with the scrolling pane, not the
 * viewport alone. Beside the details column the messages scroll inside
 * `conversation-thread-scroll`, so a bubble taller than the PANE but shorter
 * than the browser window used to satisfy neither branch — never half on
 * screen, never "taller than the root" — and could not be acknowledged at all.
 * The viewport stays in the intersection because the pane is only a scroller at
 * `@3xl`: stacked on a phone it is the page that scrolls, and using the pane
 * alone as the window would report every bubble of the thread as seen.
 *
 * `pane` is the pane's rect, or null when it is not mounted (or not passed),
 * where the viewport alone is the window — the pre-pane behaviour.
 */
export function onScreen(entry: IntersectionObserverEntry, pane: DOMRect | null): boolean {
    if (!entry.isIntersecting) return false
    if (entry.intersectionRatio >= VISIBLE_RATIO) return true

    const root = entry.rootBounds
    if (!root) return false
    const top = pane ? Math.max(root.top, pane.top) : root.top
    const bottom = pane ? Math.min(root.bottom, pane.bottom) : root.bottom
    if (bottom <= top) return false
    if (entry.boundingClientRect.height <= bottom - top) return false
    const bubbleTop = entry.boundingClientRect.top

    return bubbleTop >= top && bubbleTop <= bottom
}

export type RegisterReadNode = (messageUid: string, node: Element | null) => void

/**
 * Observes the rendered inbound-unread bubbles of one thread and acknowledges
 * the ones a human actually saw.
 *
 * Returns the registration callback `MessageItem` hands its `<li>` to — `null`
 * unregisters, which is how a message that has just been acked (or that another
 * manager's screen stamped) stops being watched.
 */
export function useReadAcknowledgement({
    org,
    uid,
    messages,
    enabled,
    scrollRootRef,
}: {
    org: string
    uid: string
    messages: ConversationMessage[]
    /**
     * False while the thread is still loading, or when the caller cannot read
     * conversations. They could not have opened the thread in that case, but the
     * guard is free.
     */
    enabled: boolean
    /**
     * The pane the messages scroll in (`conversation-thread-scroll`). Read at
     * intersection time, so it may be empty on the first render: it only sizes
     * the over-tall-bubble fallback in `onScreen`.
     */
    scrollRootRef?: RefObject<Element | null>
}): RegisterReadNode {
    const queryClient = useQueryClient()

    const acknowledge = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/read',
    )
    // The mutation object is rebuilt on every render; the ref keeps the effects
    // below from re-subscribing because of it.
    const mutateRef = useRef(acknowledge.mutate)

    /** The nodes currently mounted and worth watching, by message uid. */
    const nodesRef = useRef(new Map<string, Element>())
    /** The uids whose bubble is on screen right now. */
    const onScreenRef = useRef(new Set<string>())
    /** Running dwell timers, by uid. Cleared whenever the dwell is broken. */
    const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
    /** Uids that completed their dwell and are waiting for the next flush. */
    const pendingRef = useRef(new Set<string>())
    /** Uids already sent, so a re-render never acks the same message twice. */
    const sentRef = useRef(new Set<string>())
    const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const observerRef = useRef<IntersectionObserver | null>(null)

    /** The inbound messages of this thread the server still reports unread. */
    const unread = useMemo(() => {
        const set = new Set<string>()
        for (const message of messages) {
            if (message.direction === 'inbound' && !message.read_at && message.uid) set.add(message.uid)
        }

        return set
    }, [messages])
    const unreadRef = useRef(unread)
    const enabledRef = useRef(enabled)

    // The live values the timers and the flush read. Declared FIRST so this
    // effect commits before the ones below, which act on them.
    useEffect(() => {
        mutateRef.current = acknowledge.mutate
        unreadRef.current = unread
        enabledRef.current = enabled
    }, [acknowledge.mutate, enabled, unread])

    // The same invalidation `ConversationPanel.invalidate()` performs: the
    // thread (the bubbles lose their unread styling), the queue row's badge and
    // the sidebar counters (`/settings/conversations`, which the predicate
    // already matches) all move together.
    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]

                return key.length >= 2 && key[0] === 'get' && typeof key[1] === 'string' && key[1].includes('/conversations')
            },
        })
    }, [queryClient])

    const flush = useCallback(() => {
        flushTimerRef.current = null
        const batch = [...pendingRef.current].filter((m) => !sentRef.current.has(m) && unreadRef.current.has(m))
        pendingRef.current.clear()
        if (batch.length === 0 || !enabledRef.current) return
        for (const messageUid of batch) sentRef.current.add(messageUid)

        mutateRef.current(
            {
                params: { path: { orgCode: org, conversationUid: uid } },
                body: { message_uids: batch },
            },
            {
                onSuccess: invalidate,
                // A failed ack is dropped, not retried in a loop: the messages
                // stay unread and the next scroll or re-open acks them again.
                onError: () => {
                    for (const messageUid of batch) sentRef.current.delete(messageUid)
                },
            },
        )
    }, [invalidate, org, uid])

    const scheduleFlush = useCallback(() => {
        if (flushTimerRef.current !== null) return
        flushTimerRef.current = setTimeout(flush, DEBOUNCE_MS)
    }, [flush])

    const clearTimer = useCallback((messageUid: string) => {
        const timer = timersRef.current.get(messageUid)
        if (timer !== undefined) {
            clearTimeout(timer)
            timersRef.current.delete(messageUid)
        }
    }, [])

    /**
     * Brings the dwell timers in line with what is on screen and whether anyone
     * is looking. Called on every intersection change, and on every
     * visibility/focus change — which is what makes leaving the tab cancel a
     * pending dwell and coming back restart it.
     */
    const syncTimers = useCallback(() => {
        if (!enabledRef.current || !looking()) {
            for (const timer of timersRef.current.values()) clearTimeout(timer)
            timersRef.current.clear()

            return
        }
        for (const messageUid of onScreenRef.current) {
            if (timersRef.current.has(messageUid)) continue
            if (sentRef.current.has(messageUid) || !unreadRef.current.has(messageUid)) continue
            timersRef.current.set(
                messageUid,
                setTimeout(() => {
                    timersRef.current.delete(messageUid)
                    // Re-checked at the end of the dwell: the window may have
                    // lost focus a millisecond ago, and the server may have
                    // stamped the message meanwhile.
                    if (!looking() || sentRef.current.has(messageUid) || !unreadRef.current.has(messageUid)) return
                    pendingRef.current.add(messageUid)
                    scheduleFlush()
                }, DWELL_MS),
            )
        }
    }, [scheduleFlush])

    // One observer for the whole list, rebuilt when the thread changes.
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') return

        const observer = new IntersectionObserver(
            (entries) => {
                // Measured once per batch, not per entry: every entry of a
                // batch is laid out against the same pane.
                const pane = scrollRootRef?.current?.getBoundingClientRect() ?? null
                for (const entry of entries) {
                    const messageUid = (entry.target as HTMLElement).dataset.ackMessageUid
                    if (!messageUid) continue
                    if (onScreen(entry, pane)) {
                        onScreenRef.current.add(messageUid)
                    } else {
                        onScreenRef.current.delete(messageUid)
                        clearTimer(messageUid)
                    }
                }
                syncTimers()
            },
            // 0 so a bubble taller than the viewport reports at all, 0.5 for
            // the ordinary case.
            { threshold: [0, VISIBLE_RATIO] },
        )
        observerRef.current = observer
        for (const node of nodesRef.current.values()) observer.observe(node)

        return () => {
            observer.disconnect()
            observerRef.current = null
        }
    }, [clearTimer, scrollRootRef, syncTimers, uid])

    // A new thread is a clean slate: no dwell, no pending uid and no
    // already-sent uid of the previous thread may be acked against this one.
    //
    // `nodesRef` is deliberately NOT cleared here. The panel is not keyed by
    // uid, so switching to a thread whose detail query is still cached keeps
    // this component mounted: React attaches the NEW <li> refs during that
    // commit, BEFORE any passive effect, and this cleanup runs after them — so
    // clearing the map threw away the nodes that had just registered and the
    // observer below re-armed on an empty map, leaving the revisited thread
    // unobserved. (In the running console a later commit happened to re-attach
    // the refs and papered over it; that is an accident of an extra render, not
    // a guarantee.) The ref callbacks own this map on their own: an unmounted
    // bubble arrives here as `null` and removes itself.
    useEffect(() => {
        const onScreenNow = onScreenRef.current
        const timers = timersRef.current
        const pending = pendingRef.current
        const sent = sentRef.current

        return () => {
            for (const timer of timers.values()) clearTimeout(timer)
            timers.clear()
            onScreenNow.clear()
            pending.clear()
            sent.clear()
            if (flushTimerRef.current !== null) {
                clearTimeout(flushTimerRef.current)
                flushTimerRef.current = null
            }
        }
    }, [uid])

    // Leaving the tab, minimizing or locking the screen cancels a pending
    // dwell; coming back restarts it from zero.
    useEffect(() => {
        const onChange = () => syncTimers()
        document.addEventListener('visibilitychange', onChange)
        window.addEventListener('blur', onChange)
        window.addEventListener('focus', onChange)

        return () => {
            document.removeEventListener('visibilitychange', onChange)
            window.removeEventListener('blur', onChange)
            window.removeEventListener('focus', onChange)
        }
    }, [syncTimers])

    // Re-arm on every message-list change: a message the server has since
    // stamped (another manager's screen, or our own ack landing) is dropped from
    // the pending set WITHOUT a request, and a fresh inbound message that just
    // rendered starts its own dwell.
    useEffect(() => {
        for (const messageUid of [...pendingRef.current]) {
            if (!unread.has(messageUid)) pendingRef.current.delete(messageUid)
        }
        for (const messageUid of [...timersRef.current.keys()]) {
            if (!unread.has(messageUid)) clearTimer(messageUid)
        }
        syncTimers()
    }, [clearTimer, syncTimers, unread])

    return useCallback(
        (messageUid: string, node: Element | null) => {
            if (!messageUid) return
            if (!node) {
                const previous = nodesRef.current.get(messageUid)
                if (previous) {
                    observerRef.current?.unobserve(previous)
                    nodesRef.current.delete(messageUid)
                }
                onScreenRef.current.delete(messageUid)
                clearTimer(messageUid)

                return
            }
            ;(node as HTMLElement).dataset.ackMessageUid = messageUid
            nodesRef.current.set(messageUid, node)
            observerRef.current?.observe(node)
        },
        [clearTimer],
    )
}
